/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';

import axios, { ACCOUNT_ME_URL, getApi, putApi, takePrefetchedMyAccount } from 'lib/axios';
import { AUTH_COOKIE_ONLY, BASE_URL, HTTP_STATUS } from 'utils/const';

import { enqueueSnackbar } from './notificationSlice';

/**
 * 管理者情報を全件取得する
 */
export const fetchAllAdministrators = createAsyncThunk(
  'administrators/fetchAllAdministrators',
  async (data, { dispatch }) => {
    const param = {
      per_page: data.rowsPerPage,
      page: data.page,
      status_mail_unchecked: data.mailUnchecked === 'true' ? '1' : '0',
      status_approval_pending: data.approvalPending === 'true' ? '1' : '0',
      status_valid: data.valid === 'true' ? '1' : '0',
      status_invalid: data.invalid === 'true' ? '1' : '0',
      admin_name: data.name,
      email: data.email,
    };

    try {
      const res = await getApi(`${BASE_URL}/admins`, param);

      return res; // action.payloadとしてReducerで読み取れる
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * 管理者詳細を取得する
 */
export const fetchAdministrator = createAsyncThunk(
  'administrators/fetchAdministrator',
  async (id) => {
    const res = await getApi(`${BASE_URL}/admins/${id}`);

    console.log(res);

    return {
      id,
      admin: res.data,
    };
  },
);

/**
 * state.administrators.myAccountStatus の値
 */
export const MY_ACCOUNT_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
});

/**
 * 自身のアカウントを取得する (GET /account/me)
 *
 * action.payload は `{ admin_id, admin_name, email, is_super_admin (boolean), ... }` (ACCOUNT_ME_URL の data)。
 * 外部管理者も取得できる。
 *
 * 取れたアカウントは state.administrators.myAccount に残し (selectMyAccount で読む)、
 * 取れているあいだ・取りに行っているあいだは API を呼ばない (画面ごとに MainLayout がマウントされ直すため、
 * 画面を移るたびに呼んでいた)。ログイン・ログアウトで消し、次に呼んだときに取り直す。
 * 失敗したとき (rejected) は、次に呼んだときに取り直す。
 * Cookie だけで認証するときは、起動時・ログイン直後に verifySession が受け取ったアカウントを使い、API を呼ばない。
 */
export const fetchMyAccount = createAsyncThunk(
  'administrators/fetchMyAccount',
  async () => {
    const prefetched = takePrefetchedMyAccount();
    if (prefetched) {
      return prefetched;
    }

    const res = await getApi(ACCOUNT_ME_URL);

    return res.data; // action.payloadとしてReducerで読み取れる
  },
  {
    condition: (_arg, { getState }) => {
      const status = getState().administrators?.myAccountStatus;

      return status !== MY_ACCOUNT_STATUS.LOADING && status !== MY_ACCOUNT_STATUS.SUCCEEDED;
    },
  },
);

/**
 * アカウント登録
 */
export const createAdministrator = createAsyncThunk(
  'user/createAdministrator',
  async (param, { dispatch }) => {
    try {
      const params = {
        email: param.email,
        admin_name: param.name,
        password: param.password,
        tel: param.tel,
        other: param.other,
      };

      const res = await axios.post(`${BASE_URL}/account/temporary_registration`, params);

      dispatch(
        enqueueSnackbar({
          message: 'アカウント仮登録が完了しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return res;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * ログイン
 */
export const loginAdministrator = createAsyncThunk(
  'administrators/loginAdministrator',
  async (param, { dispatch }) => {
    try {
      const params = {
        email: param.email,
        password: param.password,
      };

      const res = await axios.post(`${BASE_URL}/account/login`, params);

      // 成功のトーストは LoginForm が login() (Cookie だけのときは verifySession も) を終えてから出す

      return res;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * ログアウト
 */
export const logoutAdministrator = createAsyncThunk(
  'administrators/logoutAdministrator',
  async (param, { dispatch }) => {
    try {
      let res;
      try {
        // Cookie だけで認証しているときは、401 で lib/axios がトップへ飛ばさないようにし、下で成功として扱う
        // (飛ばすと、呼び出し側 (MainLayout) の logout() と画面遷移が競合する)。
        // 既定 (Bearer) はこれまでどおり 401 でトークンを消してトップへ飛ばす
        res = await putApi(
          `${BASE_URL}/account/logout`,
          {},
          AUTH_COOKIE_ONLY ? { skipUnauthorizedRedirect: true } : {},
        );
      } catch (error) {
        // Cookie だけで認証しているとき、Cookie が既に無効 (期限切れ・別のタブでログアウト済み) なら
        // API は 401 を返す (Cookie も消される)。ログアウトしたのと同じなので成功として扱う
        if (!(AUTH_COOKIE_ONLY && error.response?.status === HTTP_STATUS.UNAUTHORIZED)) {
          throw error;
        }
        res = {};
      }

      dispatch(
        enqueueSnackbar({
          message: 'ログアウトしました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return res;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * パスワードリセットメール送信
 */
export const sendMessageByPasswordResetAdministrator = createAsyncThunk(
  'administrators/sendMessageByPasswordResetAdministrator',
  async (param, { dispatch }) => {
    try {
      const params = {
        email: param.email,
      };

      const res = await axios.put(`${BASE_URL}/account/reset_password_email`, params);

      console.log('response', res);

      dispatch(
        enqueueSnackbar({
          message: 'パスワードリセット用のメールを送信しました。ご確認ください。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return res;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * パスワードリセット
 */
export const resetPasswordAdministrator = createAsyncThunk(
  'administrators/resetPasswordAdministrator',
  async (param, { dispatch }) => {
    try {
      const params = {
        new_password: param.password,
      };

      const res = await axios.put(
        `${BASE_URL}/account/reset_password/?reset_password_token=${param.resetPasswordToken}`,
        params,
      );

      console.log('response', res);

      dispatch(
        enqueueSnackbar({
          message: 'パスワードリセットが完了しました。ログインしてください。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return res;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * メール認証
 */
export const applyActivationAdministrator = createAsyncThunk(
  'administrators/applyActivationAdministrator',
  async (param, { dispatch }) => {
    try {
      const params = {
        verification_code: param.token,
      };

      const res = await axios.put(
        `${BASE_URL}/account/apply_activation/?verification_code=${param.token}`,
        params,
      );

      console.log('response', res);

      dispatch(
        enqueueSnackbar({
          message: '認証が完了しました。管理者から承認されるのをお待ちください。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return res;
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      throw error;
    }
  },
);

/**
 * 管理者情報を更新する
 */
export const updateAdministrator = createAsyncThunk(
  'administrators/updateAdministrator',
  async (param) => {
    const body = {
      admin_name: param.admin_name,
      tel: param.tel,
      other: param.other,
      status_code: param.status_code,
    };

    const res = await putApi(`${BASE_URL}/admins/${param.id}`, body);

    return {
      id: param.id,
      admin: res.data.data,
    };
  },
);

/**
 * 管理者情報を更新する
 */
export const updateAdminAdministrator = createAsyncThunk(
  'administrators/updateAdminAdministrator',
  async (param) => {
    const body = {
      admin_name: param.admin_name,
      tel: param.tel,
      other: param.other,
      status_code: param.status_code?.toString(),
    };

    const res = await putApi(`${BASE_URL}/admins/super_admin/${param.id}`, body);

    return {
      id: param.id,
      admin: res.data.data,
    };
  },
);

/**
 * 自分のアカウントを更新したなら、次の fetchMyAccount で取り直す (表示はそれまで前の値のまま)
 */
function markMyAccountStaleIfUpdated(state, adminId) {
  if (state.myAccount && String(state.myAccount.admin_id) === String(adminId)) {
    state.myAccountStatus = MY_ACCOUNT_STATUS.IDLE;
  }
}

const administratorAdapter = createEntityAdapter({
  selectId: (e) => e.admin_id,
});

export const administratorSlice = createSlice({
  name: 'administrators',
  initialState: administratorAdapter.getInitialState({
    loading: false,
    // ログイン中の管理者 (自分)。fetchMyAccount で取得する
    myAccount: null,
    myAccountStatus: MY_ACCOUNT_STATUS.IDLE,
  }),
  reducers: {
    addTag: administratorAdapter.addOne,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllAdministrators.fulfilled, (state, action) => {
      administratorAdapter.setAll(state, action.payload.data);
      state.loading = false;
    });
    builder.addCase(fetchAllAdministrators.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAdministrator.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchAdministrator.fulfilled, (state, action) => {
      administratorAdapter.upsertOne(state, { ...action.payload.admin });
      state.loading = false;
    });
    builder.addCase(fetchMyAccount.pending, (state) => {
      state.loading = true;
      state.myAccountStatus = MY_ACCOUNT_STATUS.LOADING;
    });
    builder.addCase(fetchMyAccount.rejected, (state) => {
      state.loading = false;
      state.myAccount = null;
      state.myAccountStatus = MY_ACCOUNT_STATUS.FAILED;
    });
    builder.addCase(fetchMyAccount.fulfilled, (state, action) => {
      state.loading = false;
      state.myAccount = action.payload ?? null;
      state.myAccountStatus = MY_ACCOUNT_STATUS.SUCCEEDED;
    });
    builder.addCase(createAdministrator.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createAdministrator.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(createAdministrator.fulfilled, (state, _action) => {
      state.loading = false;
    });
    builder.addCase(loginAdministrator.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(loginAdministrator.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(loginAdministrator.fulfilled, (state, _action) => {
      state.loading = false;
      // 別の管理者のアカウントを引き継がないよう、次の fetchMyAccount で取り直す
      state.myAccount = null;
      state.myAccountStatus = MY_ACCOUNT_STATUS.IDLE;
    });
    builder.addCase(logoutAdministrator.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(logoutAdministrator.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(logoutAdministrator.fulfilled, (state, _action) => {
      state.loading = false;
      // 別の管理者のアカウントを引き継がないよう、次の fetchMyAccount で取り直す
      state.myAccount = null;
      state.myAccountStatus = MY_ACCOUNT_STATUS.IDLE;
    });
    builder.addCase(updateAdministrator.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateAdministrator.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateAdministrator.fulfilled, (state, action) => {
      administratorAdapter.updateOne(state, {
        id: action.payload.id,
        changes: { ...action.payload.admin },
      });
      state.loading = false;
      markMyAccountStaleIfUpdated(state, action.payload.id);
    });
    builder.addCase(updateAdminAdministrator.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateAdminAdministrator.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(updateAdminAdministrator.fulfilled, (state, action) => {
      administratorAdapter.updateOne(state, {
        id: action.payload.id,
        changes: { ...action.payload.admin },
      });
      state.loading = false;
      markMyAccountStaleIfUpdated(state, action.payload.id);
    });
    builder.addCase(sendMessageByPasswordResetAdministrator.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(sendMessageByPasswordResetAdministrator.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(sendMessageByPasswordResetAdministrator.fulfilled, (state, _action) => {
      state.loading = false;
    });
    builder.addCase(resetPasswordAdministrator.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(resetPasswordAdministrator.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(resetPasswordAdministrator.fulfilled, (state, _action) => {
      state.loading = false;
    });
    builder.addCase(applyActivationAdministrator.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(applyActivationAdministrator.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(applyActivationAdministrator.fulfilled, (state, _action) => {
      state.loading = false;
    });
  },
});

export const administratorSelectors = administratorAdapter.getSelectors(
  (state) => state.administrators,
);

/**
 * ログイン中の管理者 (自分) のアカウント (fetchMyAccount の結果。取れていなければ null)
 */
export const selectMyAccount = (state) => state.administrators.myAccount;

export default administratorSlice.reducer;
